/**
 * ONE VIEW 画像スライド
 * 3カラムが常に同一画像を表示し、横からのクリップワイプで次の写真へ自動で切り替える。
 * 各カラム2レイヤーのピンポンで継ぎ目なく無限ループ。jQuery不使用。
 * 画像リストはルート要素の data-images（JSON配列）から読む＝枚数変更は配列編集だけでOK。
 * @example
 * new OneViewSlider();
 * new OneViewSlider(".oneview__imgs", 4000);
 */
import type { PageModule } from "./page-module"

type OneViewColumn = {
  layers: HTMLElement[]
  active: number
}

export class OneViewSlider {
  root: HTMLElement | null
  images: string[]
  columns: OneViewColumn[]
  index: number
  interval: number
  timer: ReturnType<typeof setInterval> | null

  /**
   * @param {string} selector ルート要素のセレクタ
   * @param {number} interval 切り替え間隔(ms)
   * @param {ParentNode} scope 要素探索の起点（swup: 現在ページの <main>）
   */
  constructor(selector = ".oneview__imgs", interval = 3000, scope: ParentNode = document) {
    this.root = scope.querySelector(selector)
    this.images = []
    this.columns = []
    this.index = 0
    this.interval = interval
    this.timer = null

    if (!this.root) return

    try {
      this.images = JSON.parse(this.root.dataset.images || "[]")
    } catch (e) {
      this.images = []
    }
    // 画像が1枚以下、または motion 抑制設定なら自動再生しない
    if (this.images.length < 2) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.preload()
    this.setup()
    this.timer = window.setInterval(this.next, this.interval)
  }

  /**
   * 全画像を先読み（ワイプ時のチラつき防止）
   * @private
   */
  preload = () => {
    this.images.forEach((src) => {
      const im = new Image()
      im.src = src
    })
  }

  /**
   * 各カラムの2レイヤーを登録
   * @private
   */
  setup = () => {
    const cols = Array.from(this.root!.querySelectorAll(".oneview__imgsItem")) as HTMLElement[]
    this.columns = cols.map((col) => ({
      layers: Array.from(col.querySelectorAll(".oneview__imgsItemLayer")) as HTMLElement[],
      active: 0,
    }))
  }

  /**
   * 次の画像へワイプ切り替え（全カラム同期）
   * @private
   */
  next = () => {
    const nextIndex = (this.index + 1) % this.images.length
    const url = this.images[nextIndex]

    this.columns.forEach((col) => {
      if (col.layers.length < 2) return
      const cur = col.layers[col.active]
      const incoming = col.layers[col.active === 0 ? 1 : 0]
      const img = incoming.querySelector("img")
      if (img && img.getAttribute("src") !== url) img.setAttribute("src", url)

      // transitionを切って隠れ状態に即リセット → 現在画像の上に重ねる
      incoming.style.transition = "none"
      incoming.classList.remove("is-show")
      incoming.style.zIndex = "2"
      cur.style.zIndex = "1"
      void incoming.offsetWidth // 強制リフロー（transition再発火のため）
      incoming.style.transition = "" // CSS既定のtransitionに戻す
      incoming.classList.add("is-show") // 横からワイプイン

      col.active = col.active === 0 ? 1 : 0
    })

    this.index = nextIndex
  }

  /**
   * 自動再生を停止
   * @public
   */
  unmount = () => {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }
}

/**
 * ONE VIEW スライダーを scope 内にマウントする（swup 用）。
 * @param {HTMLElement} scope 現在ページの <main>
 * @returns {(() => void) | void} destroy 関数（対象が無ければ何もしない）
 */
export const mountOneviewSlider: PageModule = (scope) => {
  if (!scope.querySelector(".oneview__imgs")) return
  const inst = new OneViewSlider(".oneview__imgs", 3000, scope)
  return () => inst.unmount()
}
